import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { getAssociates, getAssociateProfile } from '../../services/associate';
import { getProjects } from '../../services/project';
import { getStates, getCities, getLeadStatuses, getLeadInterested, getBudgets, getSources, getInquiryStatuses } from '../../services/master';
import { customerService } from '../../services/customer';
import { leadService } from '../../services/lead';
import { Search } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip as MuiChip,
} from '@mui/material';
import AlertModal from '../../components/AlertModal';
import { useAuth } from '../../context/AuthContext';

// Simple debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Designations API removed - using direct text input instead

// Local fetchSources removed in favor of master service getSources

function normalizeAssociate(item) {
  const sponsor =
    Array.isArray(item.sponsorDetail) && item.sponsorDetail[0]
      ? item.sponsorDetail[0]
      : item.sponsor || item.sponsorInfo || item.sponsorDetails || item.sponsorData || {}; // Check multiple possible sponsor structures
  const reg =
    Array.isArray(item.registrationDetail) && item.registrationDetail[0]
      ? item.registrationDetail[0]
      : {};
  const personal =
    Array.isArray(item.personalDetail) && item.personalDetail[0]
      ? item.personalDetail[0]
      : {};
  const bank =
    Array.isArray(item.bankDetail) && item.bankDetail[0]
      ? item.bankDetail[0]
      : {};
  const other =
    Array.isArray(item.otherDetail) && item.otherDetail[0]
      ? item.otherDetail[0]
      : {};
  const nominee =
    Array.isArray(item.nomineeDetail) && item.nomineeDetail[0]
      ? item.nomineeDetail[0]
      : {};

  return {
    _id: item._id || '',
    associatedId: String(item.associateId ?? item._id ?? ''),
    name: item.name ?? '',
    email: item.email ?? '',
    mobile: item.phone ?? item.whatsapp ?? '',
    role: item.userAccess?.role ?? item.role ?? '',
    designation: item.designation ?? '',
    city: item.city ?? '',
    state: item.state ?? '',
    image: item.img ?? '',
    active: !!item.isActive,
    status: item.isActive ? 'Active' : 'Inactive',
    updatedDate: item.updatedAt ?? '',
    pan: personal.pan ?? '',
    aadhar: personal.aadhar ?? personal.addressNumber ?? '', // Use addressNumber if aadhar is not available
    joiningDate: reg.registrationDate ?? '',
    rank: item.rank || '—',
    commission: item.commission ?? '',
    bankName: bank.bank ?? bank.bankName ?? '',
    accountNumber: bank.accountNumber ?? '',
    ifscCode: bank.ifsc ?? bank.ifscCode ?? '',
    address: item.address ?? '',
    sponsorName: sponsor.sponsorName ?? sponsor.name ?? item.sponsorName ?? item.sponsor?.name ?? '',
    sponsorEmail: sponsor.sponsorEmail ?? sponsor.email ?? item.sponsorEmail ?? item.sponsor?.email ?? '',
    sponsorMobile: sponsor.sponsorMobile ?? sponsor.mobile ?? sponsor.phone ?? item.sponsorMobile ?? item.sponsor?.mobile ?? item.sponsor?.phone ?? '',
    sponsorId: sponsor.sponsorId ?? item.sponsorBy ?? item.sponsorId ?? '',
    // Additional fields that might be needed
    fatherName: personal.fatherName ?? '',
    dob: personal.dob ?? '',
    anniversary: personal.anniversary ?? '',
    qualification: personal.qualification ?? '',
    employeeType: other.employeeType ?? '',
    department: other.department ?? '',
    trialLastDate: other.trialLastDate ?? '',
    nomineeName: nominee.name ?? '',
    nomineePhone: nominee.phone ?? '',
    nomineeRelation: nominee.relation ?? '',
    loginId: item.userAccess?.loginId ?? '',
    userRole: item.userAccess?.role ?? '',
    userModules: item.userAccess?.modules ?? [],
    createdBy: item.createdBy ?? '',
    createdAt: item.createdAt ?? '',
    _raw: item,
  };
}

// interestedInOptions is now loaded from API (leadInterested state)

// budgetOptions is now loaded from API (budgetOptions state)

const AddLead = () => {
  const history = useHistory();
  const location = useLocation();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const { user: authUser } = useAuth();
  const showAlert = (message, type = 'info') => setAlertData({ open: true, message, type });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    company: '',
    designation: '',
    source: '',
    status: 'New',
    priority: 'New',
    interestedIn: '',
    budget: '',
    pinCode: '',
    city: '',
    state: '',
    address: '',

    assignedTo: '',
    assignedToId: '',
    assignedToPhone: '',

    requirement: '',
    remark: '',
    project: '', // New field for project selection
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);
  const [lookupMessage, setLookupMessage] = useState('');
  const [matchedCustomer, setMatchedCustomer] = useState(null);
  const [verifyingCustomer, setVerifyingCustomer] = useState(false);
  const [mobileError, setMobileError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [pinCodeError, setPinCodeError] = useState('');
  const [fieldsDisabled, setFieldsDisabled] = useState({
    name: false,
    email: false,
    phone: false,
    whatsapp: false,
    company: false,
    designation: false,
  });

  const associateDirectoryRef = useRef([]); // Persistent storage for associate data
  const [associateDirectory, setAssociateDirectory] = useState(associateDirectoryRef.current);
  const [selectedAssociate, setSelectedAssociate] = useState(null); // Store selected associate directly
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateSearchTimeoutRef = useRef(null);
  const associatesLoadedRef = useRef(false);

  // Designations removed - using direct text input

  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const sourcesLoadedRef = useRef(false);

  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [loadingInquiryStatuses, setLoadingInquiryStatuses] = useState(false);
  const inquiryStatusesLoadedRef = useRef(false);

  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loadingLeadStatuses, setLoadingLeadStatuses] = useState(false);
  const leadStatusesLoadedRef = useRef(false);

  const [leadInterested, setLeadInterested] = useState([]);
  const [loadingLeadInterested, setLoadingLeadInterested] = useState(false);
  const leadInterestedLoadedRef = useRef(false);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [addCityDialogOpen, setAddCityDialogOpen] = useState(false);
  const [newCityName, setNewCityName] = useState("");
  const [addingCity, setAddingCity] = useState(false);

  const [budgetOptions, setBudgetOptions] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);

  // Follow-up states
  const [leadId, setLeadId] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    type: '',
    date: new Date().toISOString().slice(0, 16),
    nextFollowUpDate: '',
    remark: ''
  });

  const customerDirectory = useMemo(
    () => [
      {
        customerId: 'CUST001',
        name: 'Rajesh Kumar',
        email: 'rajesh.kumar@example.com',
        mobile: '9876543210',
        whatsappNumber: '9876543210',
        companyName: 'Tech Solutions Pvt Ltd',
        profession: 'Software Engineer',
        propertyInterest: 'Residential',
        budget: '50-75 Lakhs',
        preferredLocation: 'Andheri, Bandra',
        address: '123, Marine Drive, Mumbai, Maharashtra - 400001',
        city: 'Mumbai',
        state: 'Maharashtra',
        remark: 'Interested in 2BHK apartments. Good payment history.',
      },
      {
        customerId: 'CUST002',
        name: 'Priya Sharma',
        email: 'priya.sharma@example.com',
        mobile: '9123456789',
        whatsappNumber: '9123456789',
        companyName: 'Fashion Hub',
        profession: 'Business Owner',
        propertyInterest: 'Commercial',
        budget: '1-2 Crores',
        preferredLocation: 'CP, Saket',
        address: '456, Connaught Place, New Delhi, Delhi - 110001',
        city: 'Delhi',
        state: 'Delhi',
        remark: 'Looking for commercial space. High budget client.',
      },
      {
        customerId: 'CUST003',
        name: 'Amit Patel',
        email: 'amit.patel@example.com',
        mobile: '9234567890',
        whatsappNumber: '9234567890',
        companyName: 'City Hospital',
        profession: 'Doctor',
        propertyInterest: 'Residential',
        budget: '75-100 Lakhs',
        preferredLocation: 'SG Highway, Satellite',
        address: '789, SG Highway, Ahmedabad, Gujarat - 380015',
        city: 'Ahmedabad',
        state: 'Gujarat',
        remark: 'Looking for 3BHK apartment. Prefers ready possession.',
      },
    ],
    [],
  );

  const filteredCities = cities.filter(city =>
    !formData.state || city.state === formData.state
  );

  // Initialize selectedAssociate when formData or associateDirectory changes
  useEffect(() => {
    if (associateDirectory.length > 0 && (formData.assignedToId || formData.assignedTo) && !selectedAssociate) {
      const matchingAssociate = associateDirectory.find(a =>
        a.id === formData.assignedToId ||
        a.associateId === formData.assignedToId ||
        a.name === formData.assignedTo
      );
      if (matchingAssociate) {
        setSelectedAssociate(matchingAssociate);
      }
    }
  }, [associateDirectory, formData.assignedToId, formData.assignedTo, selectedAssociate]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleAssociateInputChange = useCallback((_, value) => {
    setAssociateSearchInput(value);
  }, []);

  const handleAssociateChange = useCallback((_, value) => {
    setSelectedAssociate(value); // Store selected associate directly
    if (value) {
      setFormData(prev => ({
        ...prev,
        assignedTo: value.name || '',
        assignedToId: value.id || value.associateId || '',
        assignedToPhone: value.phone || value.mobile || '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assignedTo: '',
        assignedToId: '',
        assignedToPhone: '',
      }));
    }
  }, []);

  const handleAssociateOpen = useCallback(() => {
    // Load associates when dropdown opens if not already loaded
    if (!associatesLoadedRef.current && associateDirectory.length === 0) {
      loadAssociatesList('');
    }
  }, []); // No dependencies needed - uses refs and checks state inside

  const applyCustomerDetails = (customer) => {
    console.log('applyCustomerDetails called with:', customer);
    console.log('Current formData:', formData);
    if (!customer) {
      console.log('No customer data provided');
      return;
    }

    // Force update each field individually to ensure proper re-rendering
    const updates = {};

    if (customer.name && (!formData.name || formData.name.trim() === '')) {
      updates.name = customer.name;
      console.log('Setting name:', customer.name);
    }

    if (customer.email && (!formData.email || formData.email.trim() === '')) {
      updates.email = customer.email;
      console.log('Setting email:', customer.email);
    }

    // Note: API doesn't return company field, so we can't fill it
    if (customer.company && (!formData.company || formData.company.trim() === '')) {
      updates.company = customer.company;
      console.log('Setting company:', customer.company);
    }

    if (customer.profession && (!formData.designation || formData.designation.trim() === '')) {
      // Directly set the profession as designation (no API matching needed)
      updates.designation = customer.profession;
      console.log('Setting designation from customer profession:', customer.profession);
    }

    if ((customer.whatsapp || customer.phone) && (!formData.whatsapp || formData.whatsapp.trim() === '')) {
      updates.whatsapp = customer.whatsapp || customer.phone;
      console.log('Setting whatsapp:', customer.whatsapp || customer.phone);
    }

    // Update phone if different (but keep existing phone)
    if (customer.phone && formData.phone !== customer.phone) {
      updates.phone = customer.phone;
      console.log('Setting phone:', customer.phone);
    }

    // Map additional details from customer if available
    if (customer.address || customer.otherDetails?.address) {
      updates.address = customer.address || customer.otherDetails?.address;
    }
    if (customer.city) {
      updates.city = customer.city;
    }
    if (customer.state) {
      updates.state = customer.state;
    }
    if (customer.pinCode) {
      updates.pinCode = customer.pinCode;
    }
    if (customer.propertyInterest || (customer.otherDetails?.createdDetail && !formData.source)) {
      updates.source = customer.propertyInterest || customer.otherDetails?.createdDetail;
    }
    if (customer.budget && !formData.budget) {
      updates.budget = customer.budget;
    }
    if (customer.interestedIn && !formData.interestedIn) {
      updates.interestedIn = customer.interestedIn;
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));

      // Set leadId if available in customer data
      if (customer._id) {
        setLeadId(customer._id);
        fetchTimeline(customer._id);
      }

      // Only disable Name and Phone fields as requested
      setFieldsDisabled((prev) => ({
        ...prev,
        name: true,
        phone: true,
      }));

      // Clear any validation errors for the updated fields
      setErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(updates).forEach(field => {
          newErrors[field] = '';
        });
        return newErrors;
      });

      // Clear email error if email was updated
      if (updates.email) {
        setEmailError('');
      }

      // Force re-render to ensure UI updates
      setForceUpdate(prev => prev + 1);
    }

    setLookupMessage(`Details filled from ${customer.name}.`);
  };

  const lookupCustomerByPhone = async (phone) => {
    console.log('lookupCustomerByPhone called with phone:', phone);
    if (!phone || phone.trim().length < 10) {
      console.log('Phone too short or empty, clearing matched customer');
      setMatchedCustomer(null);
      setLookupMessage('');
      return;
    }

    setVerifyingCustomer(true);
    try {
      console.log('Calling customerService.verifyCustomerByPhone with:', phone);
      const result = await customerService.verifyCustomerByPhone(phone);
      console.log('Customer lookup result:', result);

      if (result && result.userExist === true) {
        // User exists - show option to autofill
        console.log('Customer properties:', {
          name: result.name,
          email: result.email,
          phone: result.phone,
          whatsapp: result.whatsapp,
          company: result.company,
          profession: result.profession
        });
        setMatchedCustomer(result);
        setLookupMessage(`Existing customer found: ${result.name}. Click "Use Customer Details" to autofill.`);
      } else if (result && result.userExist === false) {
        // User doesn't exist - allow manual entry
        setMatchedCustomer(null);
        setLookupMessage('No customer found with this phone number. Please fill the customer details manually.');
      } else {
        setMatchedCustomer(null);
        setLookupMessage('');
      }
    } catch (error) {
      console.error('Error verifying customer by phone:', error);
      setMatchedCustomer(null);
      setLookupMessage('Error verifying phone number. Please fill details manually.');
    } finally {
      setVerifyingCustomer(false);
    }
  };

  const fetchTimeline = async (id) => {
    if (!id) return;
    setLoadingTimeline(true);
    try {
      const data = await leadService.getTimeline(id);
      setTimeline(data || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleFollowUpChange = (e) => {
    const { name, value } = e.target;
    setFollowUpData(prev => ({ ...prev, [name]: value }));
    const errorKey = name === 'remark' ? 'followUpRemark' : name;
    setErrors(prev => ({ ...prev, [errorKey]: '' }));
  };

  const handleSaveFollowUp = async () => {
    if (!leadId) {
      showAlert('Please save the lead first before adding a follow-up', 'warning');
      return;
    }
    if (!followUpData.nextFollowUpDate || !followUpData.remark) {
      showAlert('Please fill all required follow-up fields', 'warning');
      return;
    }

    setFollowUpLoading(true);
    try {
      const payload = {
        leadId: leadId,
        type: followUpData.type,
        date: new Date(followUpData.date).toISOString(),
        nextFollowUpDate: new Date(followUpData.nextFollowUpDate).toISOString(),
        remark: followUpData.remark,
        doneBy: authUser?.name || 'Current User',
        createdBy: authUser?.name?.toLowerCase() || 'admin'
      };

      await leadService.saveFollowUp(payload);
      showAlert('Follow-up created successfully', 'success');
      
      // Reset form and refresh timeline
      setFollowUpData(prev => ({
        ...prev,
        nextFollowUpDate: '',
        remark: ''
      }));
      fetchTimeline(leadId);
    } catch (error) {
      console.error('Error saving follow-up:', error);
      showAlert(error.message || 'Failed to save follow-up', 'error');
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleApplyCustomerDetails = () => {
    console.log('handleApplyCustomerDetails called');
    console.log('matchedCustomer:', matchedCustomer);
    if (matchedCustomer) {
      applyCustomerDetails(matchedCustomer);
      // Remove the duplicate setLookupMessage since it's now handled in applyCustomerDetails
    } else {
      console.log('No matched customer found');
    }
  };

  const validateMobile = (mobile) => {
    if (!mobile) return false;
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  const validateEmail = (email) => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name, value) => {
    let errorMsg = '';
    switch (name) {
      case 'phone':
        if (!value || !value.trim()) {
          errorMsg = 'Phone is required';
        } else if (!validateMobile(value.replace(/\D/g, ''))) {
          errorMsg = 'Mobile number invalid';
        }
        break;
      case 'name':
        if (!value || !value.trim()) {
          errorMsg = 'Full name is required';
        }
        break;
      case 'nextFollowUpDate':
        if (!value) {
          errorMsg = 'Next follow up date is required';
        }
        break;
      case 'followUpRemark':
        if (!value || !value.trim()) {
          errorMsg = 'Follow up remark is required';
        }
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlur = (name, isFollowUp = false) => {
    const value = isFollowUp ? followUpData[name] : formData[name];
    const errorKey = name === 'remark' ? 'followUpRemark' : name;
    const errorMsg = validateField(errorKey, value);
    setErrors((prev) => ({
      ...prev,
      [errorKey]: errorMsg,
    }));
  };

  const handleChange = (e, value) => {
    let name, val;
    if (typeof e === 'string') {
      // Direct call with name and value
      name = e;
      val = value;
    } else {
      // Event object
      name = e.target.name;
      val = e.target.value;
    }
    setFormData((prev) => ({ ...prev, [name]: val }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'phone') {
      lookupCustomerByPhone(val);
    }
  };

  const handleMobileChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData((prev) => {
      const newForm = { ...prev, phone: digitsOnly };

      if (!prev.whatsapp || prev.whatsapp === prev.phone) {
        newForm.whatsapp = digitsOnly;
      }

      return newForm;
    });

    // Reset disabled state when phone changes (new customer lookup)
    if (digitsOnly !== formData.phone) {
      setFieldsDisabled({
        name: false,
        email: false,
        phone: false,
        whatsapp: false,
        company: false,
        designation: false,
      });
      setMatchedCustomer(null);
      setLookupMessage('');
    }

    setErrors((prev) => ({ ...prev, phone: '' }));

    if (digitsOnly && digitsOnly.length < 10) {
      setMobileError("Mobile number must be 10 digits");
    } else if (digitsOnly && !validateMobile(digitsOnly)) {
      setMobileError("Mobile number invalid");
    } else {
      setMobileError("");
      // Only call API when we have a valid 10-digit number
      if (digitsOnly.length === 10) {
        lookupCustomerByPhone(digitsOnly);
      }
    }
  };

  const handleWhatsappChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData((prev) => ({ ...prev, whatsapp: digitsOnly }));

    setErrors((prev) => ({ ...prev, whatsapp: '' }));

    if (digitsOnly && digitsOnly.length < 10) {
      setWhatsappError("WhatsApp number must be 10 digits");
    } else if (digitsOnly && !validateMobile(digitsOnly)) {
      setWhatsappError("Mobile number invalid");
    } else {
      setWhatsappError("");
    }
  };

  const handleEmailChange = (value) => {
    setFormData((prev) => ({ ...prev, email: value }));

    setErrors((prev) => ({ ...prev, email: '' }));

    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePinCodeChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
    setFormData((prev) => ({ ...prev, pinCode: digitsOnly }));

    setErrors((prev) => ({ ...prev, pinCode: '' }));

    if (digitsOnly && digitsOnly.length !== 6) {
      setPinCodeError("Pin code must be exactly 6 digits");
    } else {
      setPinCodeError("");
    }
  };

  const validate = () => {
    const e = {};

    if (!formData.name?.trim()) e.name = 'Full name is required';
    if (!formData.phone?.trim()) {
      e.phone = 'Phone is required';
    } else {
      if (!validateMobile(formData.phone.replace(/\D/g, ''))) {
        e.phone = 'Mobile number invalid';
      }
    }

    if (!followUpData.nextFollowUpDate) {
      e.nextFollowUpDate = 'Next follow up date is required';
    }
    if (!followUpData.remark?.trim()) {
      e.followUpRemark = 'Follow up remark is required';
    }

    if (mobileError) e.phone = mobileError;
    if (whatsappError) e.whatsapp = whatsappError;
    if (emailError) e.email = emailError;
    if (pinCodeError) e.pinCode = pinCodeError;

    if (formData.whatsapp?.trim()) {
      if (!validateMobile(formData.whatsapp.replace(/\D/g, ''))) {
        e.whatsapp = 'Mobile number invalid';
      }
    }

    if (formData.pinCode?.trim()) {
      const pinCodeRegex = /^\d{6}$/;
      if (!pinCodeRegex.test(formData.pinCode.replace(/\s/g, ''))) {
        e.pinCode = 'Pin code must be exactly 6 digits';
      }
    }

    return e;
  };

  const buildPayload = () => {
    const payload = {
      interestedIn: formData.interestedIn || undefined,
      projectInterestedIn: formData.project ? [{ projectName: formData.project }] : [], // Use selected project

      source: formData.source || undefined,
      stage: 'Lead',
      status: formData.status || 'New',

      address: {
        addressLine: formData.address || '',
        city: formData.city || '',
        state: formData.state || '',
      },
      followUp: followUpData.nextFollowUpDate
        ? {
          nextFollowUpDate: new Date(followUpData.nextFollowUpDate).toISOString(),
          lastFollowUpRemark: followUpData.remark || '',
          lastFollowUpDate: followUpData.date ? new Date(followUpData.date).toISOString() : new Date().toISOString(),
        }
        : undefined,
      remarks: {
        remark1: formData.requirement || '',
        remark2: '',
        generalRemark: formData.remark || '',
      },
      associateId: formData.assignedToId || '',

      createdBy: formData.company || 'a1 Enterprises Pvt. Ltd',
      updatedBy: 'Admin',
      isDeleted: false,
      isActive: true,
    };

    // Add customer verification status and details
    payload.customerDetails = {
      customerExist: !!(matchedCustomer && matchedCustomer.userExist === true),
      ...(matchedCustomer && matchedCustomer.userExist === true && matchedCustomer._id && {
        customerId: matchedCustomer._id
      }),
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      whatsapp: formData.whatsapp || formData.phone,
      company: formData.company,
      designation: formData.designation,
    };

    return payload;
  };

  const handleSubmit = async () => {
    setServerError('');
    const e = validate();
    setErrors(e);

    const missingFields = [];
    const fieldLabels = {
      phone: 'Phone Number',
      name: 'Full Name',
      nextFollowUpDate: 'Next Follow Up Date',
      followUpRemark: 'Follow Up Remark',
    };

    Object.keys(e).forEach((field) => {
      if (e[field]) {
        missingFields.push(fieldLabels[field] || field);
      }
    });

    if (missingFields.length > 0) {
      showAlert(`Please fill in all required fields:\n${missingFields.join(', ')}`, 'warning');

      const fieldOrder = [
        'phone',
        'name',
        'nextFollowUpDate',
        'followUpRemark',
      ];

      const firstErrorField = fieldOrder.find((field) => e[field]);
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return;
    }

    const payload = buildPayload();

    try {
      setSubmitting(true);
      const res = await fetch(`${BASE_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }

      const json = await res.json();
      console.log('Lead creation response:', json);
      
      const newLeadId = json.data?._id || json._id;

      // If follow-up data is present and we have a lead ID, call the follow-up API
      if (newLeadId && followUpData.nextFollowUpDate && followUpData.remark) {
        try {
          const followUpPayload = {
            leadId: newLeadId,
            type: followUpData.type,
            date: new Date(followUpData.date).toISOString(),
            nextFollowUpDate: new Date(followUpData.nextFollowUpDate).toISOString(),
            remark: followUpData.remark,
            doneBy: authUser?.name || 'Current User',
            createdBy: authUser?.name?.toLowerCase() || 'admin'
          };
          
          console.log('Calling follow-up API with payload:', followUpPayload);
          await leadService.saveFollowUp(followUpPayload);
        } catch (followUpErr) {
          console.error('Error saving initial follow-up:', followUpErr);
          // We don't want to fail the entire process if follow-up fails, 
          // but we should probably alert the user.
        }
      }

      showAlert('Lead added successfully!', 'Success');
      setTimeout(() => history.push('/app/lead/LeadList'), 1500);
    } catch (err) {
      setServerError(err?.message || 'Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    history.push('/app/lead/LeadList');
  };

  const loadAssociatesList = useCallback(async (searchTerm = '') => {
    if (loadingAssociates) return;

    // Don't load if already loaded and no search term (prevents unnecessary API calls)
    if (associatesLoadedRef.current && !searchTerm.trim()) return;

    setLoadingAssociates(true);
    try {
      const params = {};
      if (searchTerm) {
        params.filterKey = searchTerm;
      }
      const response = await getAssociates(params);
      const associates = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.docs || response?.docs || []);

      const normalizedAssociates = associates.map(normalizeAssociate);

      const formattedAssociates = normalizedAssociates.map(associate => ({
        id: associate._id,
        associateId: associate.associatedId,
        name: associate.name || '',
        mobile: associate.mobile || '',
        phone: associate.mobile || '',
        displayLabel: `${associate.name || ''} — ${associate.mobile || ''}`,
        fullData: associate,
      }));

      // Update persistent ref and state
      associateDirectoryRef.current = formattedAssociates;
      setAssociateDirectory(formattedAssociates);
      if (!searchTerm) {
        associatesLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to load associates list:', error);
      // Preserve existing associates from ref on API errors
      if (associateDirectoryRef.current.length > 0) {
        setAssociateDirectory(associateDirectoryRef.current);
      } else if (!associatesLoadedRef.current) {
        setAssociateDirectory([]);
      }
    } finally {
      setLoadingAssociates(false);
    }
  }, []); // Remove loadingAssociates dependency to prevent re-creation

  // Stabilize the search effect to prevent excessive re-renders
  const debouncedAssociateSearch = useMemo(() => {
    return debounce((searchValue) => {
      const trimmedValue = searchValue.trim();
      // Only load associates if:
      // 1. We have a search term (for filtering), OR
      // 2. We haven't loaded associates yet (initial load)
      if (trimmedValue || !associatesLoadedRef.current) {
        loadAssociatesList(trimmedValue);
      }
    }, 500);
  }, []); // Keep no dependencies to prevent recreation on every associateDirectory change

  // Unused legacy lookup functions removed

  useEffect(() => {
    // Only load associates once when component mounts and none are loaded
    if (!associatesLoadedRef.current && !loadingAssociates && associateDirectory.length === 0) {
      loadAssociatesList('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount


  useEffect(() => {
    if (associateSearchTimeoutRef.current) {
      clearTimeout(associateSearchTimeoutRef.current);
    }

    associateSearchTimeoutRef.current = setTimeout(() => {
      debouncedAssociateSearch(associateSearchInput);
    }, 300); // Consistent delay for all searches

    return () => {
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associateSearchInput]); // Remove debouncedAssociateSearch from dependencies to prevent recreation issues

  useEffect(() => {
    return () => {
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (formData.phone) {
      lookupCustomerByPhone(formData.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to auto-fill logged-in associate as "Assigned To"
  useEffect(() => {
    const fetchAndSetDefaultAssociate = async () => {
      // Only set default if no assignment is already present (e.g., from location state)
      if (formData.assignedToId || formData.assignedTo || selectedAssociate) return;

      try {
        const response = await getAssociateProfile();
        if (response.status && response.data) {
          const associate = normalizeAssociate(response.data);
          const formatted = {
            id: associate._id,
            associateId: associate.associatedId,
            name: associate.name || '',
            mobile: associate.mobile || '',
            phone: associate.mobile || '',
            displayLabel: `${associate.name || ''} — ${associate.mobile || ''}`,
            fullData: associate,
          };

          // Add to directory so Autocomplete recognizes it
          setAssociateDirectory(prev => {
            const exists = prev.some(a => a.id === formatted.id);
            if (exists) return prev;
            return [formatted, ...prev];
          });
          associateDirectoryRef.current = [formatted, ...associateDirectoryRef.current];

          // Set as selected
          setSelectedAssociate(formatted);
          setFormData(prev => ({
            ...prev,
            assignedTo: formatted.name,
            assignedToId: formatted.id,
            assignedToPhone: formatted.phone,
          }));
        }
      } catch (error) {
        console.error('Failed to set default login associate:', error);
      }
    };

    // Delay slightly to follow master data or state loading
    if (!location.state?.visitData && !location.state?.leadData) {
      fetchAndSetDefaultAssociate();
    }
  }, [location.state]);

  useEffect(() => {
    const loadMasterData = async () => {
      setLoadingBudgets(true);
      setLoadingProjects(true);
      try {
        const [
          fetchedStates,
          fetchedCities,
          fetchedSources,
          fetchedInquiryStatuses,
          fetchedStatuses,
          fetchedInterested,
          fetchedBudgets,
          fetchedProjects
        ] = await Promise.all([
          getStates(),
          getCities(),
          getSources(),
          getInquiryStatuses(),
          getLeadStatuses(),
          getLeadInterested(),
          getBudgets(),
          getProjects()
        ]);

        setStates(fetchedStates);
        setCities(fetchedCities);
        setSources(fetchedSources);

        // Filter by stageType 'Lead' and handle unique statuses
        const leadStatusesData = (fetchedInquiryStatuses || []).filter(item => (item.stageType || '').toLowerCase() === 'lead');
        const unique = Array.from(new Map(leadStatusesData.map(item => [(item.statusName || '').trim().toUpperCase(), item])).values());
        setInquiryStatuses(unique);

        // Auto-select 'Hot' from inquiry statuses (case-insensitive) - removed as per request
        // const hotStatus = unique.find(s => (s.statusName || '').toUpperCase() === 'HOT');
        // if (hotStatus) {
        //   setFollowUpData(prev => ({ ...prev, type: hotStatus.statusName }));
        // }

        setLeadStatuses(fetchedStatuses);
        
        // Auto-select 'New' from lead statuses (case-insensitive)
        const initialStatus = (fetchedStatuses || []).find(s => (s.leadStatus || '').toUpperCase() === 'NEW');
        if (initialStatus) {
          setFormData(prev => ({ ...prev, status: initialStatus.leadStatus }));
        }
        setLeadInterested(fetchedInterested);
        setBudgetOptions(fetchedBudgets);
        setProjects(fetchedProjects);
      } catch (error) {
        console.error("Failed to load master data:", error);
      } finally {
        setLoadingBudgets(false);
        setLoadingProjects(false);
      }
    };

    loadMasterData();
  }, []);

  // Effect to handle data passed from Site Visit or other pages
  useEffect(() => {
    if (location.state?.visitData) {
      const visit = location.state.visitData;
      setFormData(prev => ({
        ...prev,
        name: visit.name || prev.name,
        phone: visit.phone || prev.phone,
        whatsapp: visit.whatsapp || visit.phone || prev.whatsapp,
        email: visit.email || prev.email,
        address: visit.address || prev.address,
        city: visit.city || prev.city,
        state: visit.state || prev.state,
        source: visit.source || 'Site Visit',
        interestedIn: visit.interestedIn || visit.projectInterested || prev.interestedIn,
        budget: visit.budgetUpto || prev.budget,
        project: visit.projectName || visit.project || prev.project,
        remark: visit.visitRemark || visit.lastFollowUpRemark || prev.remark,
      }));

      if (visit.phone) {
        lookupCustomerByPhone(visit.phone);
      }
    } else if (location.state?.leadData) {
      // Handle lead data if passed
      const lead = location.state.leadData;
      setLeadId(lead._id || lead.id);
      if (lead._id || lead.id) fetchTimeline(lead._id || lead.id);

      setFormData(prev => ({
        ...prev,
        name: lead.name || prev.name,
        phone: lead.phone || lead.contactNumber || prev.phone,
        email: lead.email || prev.email,
        source: lead.source || prev.source,
      }));
    }
  }, [location.state]);

  // Designation re-matching removed - direct text input now used

  const handleCloseAddCityDialog = () => {
    setAddCityDialogOpen(false);
    setNewCityName("");
  };

  const handleAddCity = async () => {
    if (!newCityName.trim() || !formData.state) {
      return;
    }

    setAddingCity(true);
    try {
      const response = await fetch(`${BASE_URL}/api/globalMasterDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "city",
          details: {
            cityName: newCityName.trim(),
            state: formData.state,
          },
          status: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add city");
      }

      const result = await response.json();

      const newCity = {
        id: result.data?._id || Date.now().toString(),
        name: newCityName.trim(),
        value: newCityName.trim(),
        state: formData.state,
      };

      setCities((prev) => [...prev, newCity]);
      setFormData((prev) => ({ ...prev, city: newCity.value }));
      handleCloseAddCityDialog();
    } catch (error) {
      console.error("Error adding city:", error);
    } finally {
      setAddingCity(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: '#fff' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h5' sx={{ fontWeight: 700 }}>
            Add New Lead
          </Typography> 
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {serverError ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        ) : null}
        {lookupMessage || verifyingCustomer ? (
          <Alert
            severity={verifyingCustomer ? 'info' : matchedCustomer ? 'success' : 'info'}
            sx={{ mb: matchedCustomer ? 1 : 2, display: 'flex', alignItems: 'center', gap: 2 }}
            action={null}
          >
            {verifyingCustomer ? (
              <>
                <CircularProgress size={16} />
                Verifying customer...
              </>
            ) : (
              lookupMessage
            )}
          </Alert>
        ) : null}

        {matchedCustomer ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant='contained'
              size='small'
              onClick={handleApplyCustomerDetails}
              disabled={verifyingCustomer}
            >
              Use Customer Details
            </Button>
          </Box>
        ) : null}

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography
              variant='h6'
              sx={{ fontWeight: 700, color: '#1976d2' }}
            >
              Personal Information
            </Typography>
            {matchedCustomer && (
              <Typography
                variant='caption'
                sx={{
                  color: '#666',
                  fontStyle: 'italic',
                  bgcolor: '#f5f5f5',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1
                }}
              >
                Fields auto-filled from existing customer
              </Typography>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} id="field-phone">
              <TextField
                fullWidth
                label='Phone Number'
                name='phone'
                value={formData.phone}
                onChange={(e) => handleMobileChange(e.target.value)}
                onBlur={() => handleFieldBlur('phone')}
                size='small'
                error={!!errors.phone || !!mobileError}
                helperText={errors.phone || mobileError || "10 digits (starts with 6-9)"}
                required
                disabled={fieldsDisabled.phone}
                inputProps={{
                  maxLength: 10,
                  pattern: '[6-9][0-9]{9}',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <PhoneIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-name">
              <TextField
                fullWidth
                label='Full Name'
                name='name'
                value={formData.name}
                onChange={handleChange}
                onBlur={() => handleFieldBlur('name')}
                size='small'
                error={!!errors.name}
                helperText={errors.name}
                required
                disabled={fieldsDisabled.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label='Email'
                name='email'
                type='email'
                value={formData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                size='small'
                error={!!errors.email || !!emailError}
                helperText={errors.email || emailError}
                disabled={fieldsDisabled.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <EmailIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label='WhatsApp Number'
                name='whatsapp'
                value={formData.whatsapp}
                onChange={(e) => handleWhatsappChange(e.target.value)}
                size='small'
                error={!!errors.whatsapp || !!whatsappError}
                helperText={errors.whatsapp || whatsappError || "10 digits (starts with 6-9)"}
                disabled={fieldsDisabled.whatsapp}
                inputProps={{
                  maxLength: 10,
                  pattern: '[6-9][0-9]{9}',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label='Company'
                name='company'
                value={formData.company}
                onChange={handleChange}
                size='small'
                disabled={fieldsDisabled.company}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BusinessIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label='Designation'
                name='designation'
                value={formData.designation}
                onChange={handleChange}
                size='small'
                disabled={fieldsDisabled.designation}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <WorkIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Budget</InputLabel>
                <Select
                  name='budget'
                  value={formData.budget}
                  onChange={handleChange}
                  label='Budget'
                >
                  {budgetOptions.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.budgetRange}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}
          >
            Lead Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
                required
                error={!!errors.source}
              >
                <InputLabel>Source</InputLabel>
                <Select
                  name='source'
                  value={formData.source}
                  onChange={handleChange}
                  label='Source'
                >
                  {sources.map((source) => (
                    <MenuItem key={source.id} value={source.name}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.source ? (
                <Typography variant='caption' color='error'>
                  {errors.source}
                </Typography>
              ) : null}
            </Grid> */}

            {/* Project Dropdown */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
              >
                <InputLabel>Source </InputLabel>
                <Select
                  name='source'
                  value={formData.source || ''}
                  onChange={handleChange}
                  label='Source'
                  error={!!errors.source}
                >
                  {sources.map((source) => (
                    <MenuItem key={source.id} value={source.name}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Status </InputLabel>
                <Select
                  name='status'
                  value={formData.status}
                  onChange={handleChange}
                  label='Status'
                >
                  {leadStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.leadStatus}>
                      {status.leadStatus}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Status</InputLabel>
                <Select
                  name='status'
                  value={formData.status}
                  onChange={handleChange}
                  label='Status'
                >
                  {inquiryStatuses.map((priority) => (
                    <MenuItem key={priority.id} value={priority.statusName}>
                      {priority.statusName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
                error={!!errors.interestedIn}
              >
                <InputLabel>Interested In</InputLabel>
                <Select
                  name='interestedIn'
                  value={formData.interestedIn}
                  onChange={handleChange}
                  label='Interested In'
                >
                  {leadInterested.map((option) => (
                    <MenuItem key={option.id} value={option.leadInterested}>
                      {option.leadInterested}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.interestedIn ? (
                <Typography variant='caption' color='error'>
                  {errors.interestedIn}
                </Typography>
              ) : null}
            </Grid>



            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
                required
                error={!!errors.budget}
              > */}
            {/* <InputLabel>Budget *</InputLabel>
                <Select
                  name='budget'
                  value={formData.budget}
                  onChange={handleChange}
                  label='Budget *'
                >
                  {budgetOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select> */}
            {/* </FormControl>
              {errors.budget ? (
                <Typography variant='caption' color='error'>
                  {errors.budget}
                </Typography>
              ) : null}
            </Grid> */}

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                key={associateDirectory.length > 0 ? 'loaded' : 'empty'} // Stable key to prevent state reset
                fullWidth
                size="small"
                options={associateDirectory}
                getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.mobile || ''}`}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={loadingAssociates}
                inputValue={associateSearchInput}
                onInputChange={handleAssociateInputChange}
                onOpen={handleAssociateOpen}
                value={selectedAssociate}
                onChange={handleAssociateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned To"
                    placeholder="Search Associate (Name / Mobile)"
                    error={!!errors.assignedTo}
                    helperText={errors.assignedTo}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Follow Up Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="type"
                  value={followUpData.type}
                  onChange={handleFollowUpChange}
                  label="Type"
                >
                  {inquiryStatuses.map((priority) => (
                    <MenuItem key={priority.id} value={priority.statusName}>
                      {priority.statusName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Follow Up Date"
                name="date"
                type="datetime-local"
                value={followUpData.date}
                onChange={handleFollowUpChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} id="field-nextFollowUpDate">
              <TextField
                fullWidth
                label="Next Follow Up Date"
                name="nextFollowUpDate"
                type="datetime-local"
                value={followUpData.nextFollowUpDate}
                onChange={handleFollowUpChange}
                onBlur={() => handleFieldBlur('nextFollowUpDate', true)}
                error={!!errors.nextFollowUpDate}
                helperText={errors.nextFollowUpDate}
                size="small"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={8} id="field-followUpRemark">
              <TextField
                fullWidth
                label="Remark"
                name="remark"
                value={followUpData.remark}
                onChange={handleFollowUpChange}
                onBlur={() => handleFieldBlur('remark', true)}
                error={!!errors.followUpRemark}
                helperText={errors.followUpRemark}
                size="small"
                multiline
                rows={2}
                placeholder="Enter follow-up notes..."
                required
              />
            </Grid>
            {leadId && (
              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveFollowUp}
                  disabled={followUpLoading}
                  startIcon={followUpLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  sx={{ height: 40 }}
                >
                  Save Follow Up
                </Button>
              </Grid>
            )}
          </Grid>

          {leadId && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#1976d2' }}>
                Follow Up History
              </Typography>
              {loadingTimeline ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress size={32} />
                </Box>
              ) : timeline.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Next Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Done By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeline.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{item.date ? new Date(item.date).toLocaleString('en-GB') : '-'}</TableCell>
                          <TableCell>
                            <MuiChip 
                              label={item.type} 
                              size="small" 
                              color={item.type?.toUpperCase() === 'HOT' ? 'error' : item.type?.toUpperCase() === 'WARM' ? 'warning' : 'primary'}
                              variant="outlined" 
                              sx={{ fontWeight: 600, fontSize: '11px' }}
                            />
                          </TableCell>
                          <TableCell>{item.remark}</TableCell>
                          <TableCell>{item.nextFollowUpDate ? new Date(item.nextFollowUpDate).toLocaleString('en-GB') : '-'}</TableCell>
                          <TableCell>{item.doneBy || item.createdBy || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  No follow-up records found.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Location Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="State"
                select
                fullWidth
                size="small"
                value={formData.state}
                onChange={(e) => {
                  handleChange("state", e.target.value);
                  handleChange("city", "");
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.value}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="City"
                select
                fullWidth
                size="small"
                value={formData.city}
                onChange={(e) => {
                  if (e.target.value === "__add_city__") {
                    setAddCityDialogOpen(true);
                    setNewCityName("");
                  } else {
                    handleChange("city", e.target.value);
                  }
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                disabled={!formData.state}
                helperText={!formData.state ? "Please select a state first" : ""}
              >
                <MenuItem value="__add_city__" sx={{ color: "#1976d2", fontWeight: 500 }}>
                  ➕ Add New City
                </MenuItem>
                {filteredCities.map((city) => (
                  <MenuItem key={city.id} value={city.value}>
                    {city.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Pin Code"
                name="pinCode"
                value={formData.pinCode}
                onChange={(e) => handlePinCodeChange(e.target.value)}
                size="small"
                error={!!errors.pinCode || !!pinCodeError}
                helperText={errors.pinCode || pinCodeError}
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]{6}',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
                placeholder="Enter complete address"
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requirement Details"
                name="requirement"
                value={formData.requirement}
                onChange={handleChange}
                size="small"
                multiline
                rows={3}
                placeholder="Describe the customer's requirements..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remark"
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
                placeholder="Any additional notes..."
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}
        >
          <Button variant='outlined' onClick={handleBack} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            startIcon={
              submitting ? (
                <CircularProgress size={18} color='inherit' />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              '&.Mui-disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            {submitting ? 'Saving...' : 'Save Lead'}
          </Button>
        </Box>
      </Paper>

      <Dialog open={addCityDialogOpen} onClose={handleCloseAddCityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New City</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                disabled
                helperText="City will be added to this state"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="City Name"
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCityDialog} disabled={addingCity}>
            Cancel
          </Button>
          <Button
            onClick={handleAddCity}
            variant="contained"
            disabled={!newCityName.trim() || addingCity}
          >
            {addingCity ? "Adding..." : "Add City"}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={alertData.open}
        onClose={() => setAlertData({ ...alertData, open: false })}
        title={alertData.type}
        message={alertData.message}
        type={alertData.type.toLowerCase() === 'success' ? 'success' : alertData.type.toLowerCase() === 'error' ? 'error' : alertData.type.toLowerCase() === 'warning' ? 'warning' : 'info'}
        disableRestoreFocus={true}
      />
    </Box>
  );
};

export default AddLead;
